import { DegradablePromise, DegradeError } from '@atlassian/search-client/degradable-promise';

import { PRIMARY_PRODUCT } from './constants';
import { getInputIntentRemoteConvergence } from './remote-rules-engine-convergence/get-input-intent-remote';
import {
	getInputIntentRemoteJira,
	type JiraRemoteIntentOptions,
} from './remote-rules-engine-jira/get-input-intent-remote';
import { type ConvergenceRemoteIntentOptions, type IntentResponse } from './types';

export const REMOTE_INTENT_TIMEOUT_MS = 500;

export class IntentDetectionTimeoutError extends Error {
	constructor(public readonly timeoutMs: number) {
		super(`Remote intent detection timed out after ${timeoutMs}ms`);
		this.name = 'IntentDetectionTimeoutError';
	}
}

export type RemoteIntentContext =
	| ({
			primaryProduct: typeof PRIMARY_PRODUCT.JIRA;
	  } & JiraRemoteIntentOptions)
	| ConvergenceRemoteIntentOptions;

export const withRemoteIntentTimeout = async <T>(
	promise: Promise<T>,
	timeoutMs: number = REMOTE_INTENT_TIMEOUT_MS,
): Promise<T> => {
	try {
		return await DegradablePromise.from(promise, () => {}, timeoutMs).promise();
	} catch (error) {
		if (error instanceof DegradeError) {
			throw new IntentDetectionTimeoutError(timeoutMs);
		}

		throw error;
	}
};

/** Selects the product-specific remote engine and applies the shared timeout. */
export const getInputIntentRemote = (
	input: string,
	context: RemoteIntentContext,
	timeoutMs: number = REMOTE_INTENT_TIMEOUT_MS,
): Promise<IntentResponse> => {
	const intentPromise =
		context.primaryProduct === PRIMARY_PRODUCT.JIRA
			? getInputIntentRemoteJira(input, {
					siteId: context.siteId,
					orgId: context.orgId,
					sessionId: context.sessionId,
					model: context.model,
					headers: context.headers,
				})
			: getInputIntentRemoteConvergence(input, {
					primaryProduct: context.primaryProduct,
					cloudId: context.cloudId,
					locale: context.locale,
					model: context.model,
					onNetworkRequest: context.onNetworkRequest,
				});

	return withRemoteIntentTimeout(intentPromise, timeoutMs);
};
